import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  Box, Typography, Paper, List, ListItem, ListItemButton, ListItemText, ListItemIcon,
  LinearProgress, Button, Chip, Divider,
} from '@mui/material'
import {
  CheckCircle2, Eye, AlertTriangle, Clock, Play,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import {
  openConsolidationPreview,
  startExecution,
} from '../../../../redux/actions/boConsolidationAction'
import BOConsolidationPreview from './BOConsolidationPreview'

/**
 * BOConsolidationPreviewPanel — main view for the 'consolidation-preview' phase.
 *
 * Renders:
 *   - Bulk progress bar (when bulkLoading)
 *   - Sidebar list of all 'update' docs with status badges
 *   - Auto-opens BOConsolidationPreview for the first previewed doc
 *   - Bottom CTA: "Execute Approved" enabled only when all updates are confirmed
 */
export default function BOConsolidationPreviewPanel() {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const selectedBo = useSelector((s) => s.boConsolidation?.selectedBo)
  const extractedDocs = useSelector((s) => s.boConsolidation?.extractedDocs) || { new: [], update: [], abroge: [] }
  const consolidationPreview = useSelector((s) => s.boConsolidation?.consolidationPreview) || {
    bulkLoading: false,
    bulkProgress: { step: '', percent: 0, message: '', current: 0, total: 0 },
    currentDocId: null,
  }

  const updates = extractedDocs.update || []
  const eligible = updates.filter((d) =>
    ['approved', 'previewed', 'preview_failed', 'confirmed'].includes(d.validation_status)
  )

  const counts = {
    approved: eligible.filter((d) => d.validation_status === 'approved').length,
    previewed: eligible.filter((d) => d.validation_status === 'previewed').length,
    preview_failed: eligible.filter((d) => d.validation_status === 'preview_failed').length,
    confirmed: eligible.filter((d) => d.validation_status === 'confirmed').length,
  }

  const allConfirmed = eligible.length > 0 && counts.confirmed === eligible.length
  const someUnconfirmed = counts.approved + counts.previewed + counts.preview_failed > 0

  const bulkProgress = consolidationPreview.bulkProgress
  const isBulkLoading = consolidationPreview.bulkLoading

  // Auto-open the first previewed (or approved) doc once bulk is done
  useEffect(() => {
    if (!isBulkLoading && consolidationPreview.currentDocId === null && eligible.length > 0) {
      const next = eligible.find((d) => d.validation_status === 'previewed')
        || eligible.find((d) => d.validation_status === 'approved')
        || eligible[0]
      if (next) dispatch(openConsolidationPreview(next.id))
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isBulkLoading])

  const handleExecute = () => {
    if (selectedBo?.id) {
      dispatch(startExecution(selectedBo.id))
    }
  }

  const totalApproved = ['new', 'update', 'abroge'].reduce((sum, key) => {
    const docs = extractedDocs[key] || []
    return sum + docs.filter((d) => {
      if (key === 'update') return d.validation_status === 'confirmed'
      return d.validation_status === 'approved'
    }).length
  }, 0)

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* Bulk progress bar */}
      {isBulkLoading && (
        <Paper sx={{ p: 2.5, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {t('du_bo_generating_previews', 'Generating consolidation previews...')}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {bulkProgress.current}/{bulkProgress.total} ({Math.round(bulkProgress.percent)}%)
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={bulkProgress.percent}
            sx={{
              height: 6, borderRadius: 3,
              backgroundColor: 'rgba(201,169,110,0.15)',
              '& .MuiLinearProgress-bar': { backgroundColor: '#c9a96e' },
            }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            {bulkProgress.message}
          </Typography>
        </Paper>
      )}

      {/* Sidebar list of all updates */}
      <Paper sx={{ borderRadius: 2 }}>
        <Box sx={{ p: 2.5, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
            {t('du_bo_consolidations_review', 'Consolidations to review')} ({eligible.length})
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
            {counts.confirmed > 0 && (
              <Chip
                size="small"
                icon={<CheckCircle2 size={12} />}
                label={`${counts.confirmed} ${t('du_bo_confirmed', 'confirmed')}`}
                sx={{ backgroundColor: 'rgba(76,175,80,0.12)', color: '#4caf50' }}
              />
            )}
            {counts.previewed > 0 && (
              <Chip
                size="small"
                icon={<Eye size={12} />}
                label={`${counts.previewed} ${t('du_bo_status_previewed', 'preview ready')}`}
                color="info"
              />
            )}
            {counts.approved > 0 && (
              <Chip
                size="small"
                icon={<Clock size={12} />}
                label={`${counts.approved} ${t('du_bo_status_approved', 'awaiting preview')}`}
              />
            )}
            {counts.preview_failed > 0 && (
              <Chip
                size="small"
                icon={<AlertTriangle size={12} />}
                label={`${counts.preview_failed} ${t('du_bo_status_preview_failed', 'failed')}`}
                color="error"
              />
            )}
          </Box>
        </Box>

        <List dense sx={{ maxHeight: 480, overflow: 'auto', py: 0 }}>
          {eligible.length === 0 && (
            <ListItem>
              <ListItemText
                primary={t('du_bo_no_consolidations', 'No consolidations to review.')}
                primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
              />
            </ListItem>
          )}
          {eligible.map((doc, idx) => (
            <React.Fragment key={doc.id}>
              {idx > 0 && <Divider component="li" />}
              <ListItemButton
                onClick={() => dispatch(openConsolidationPreview(doc.id))}
                sx={{
                  py: 1.5,
                  '&:hover': { backgroundColor: 'rgba(201,169,110,0.04)' },
                }}
              >
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <RowStatusIcon status={doc.validation_status} />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {doc.doc_type} {doc.doc_number}
                      </Typography>
                      <RowStatusChip status={doc.validation_status} t={t} />
                    </Box>
                  }
                  secondary={
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        display: '-webkit-box',
                        WebkitLineClamp: 1,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }}
                    >
                      {doc.title}
                    </Typography>
                  }
                />
              </ListItemButton>
            </React.Fragment>
          ))}
        </List>

        {/* Execute bar */}
        <Box
          sx={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            p: 2.5, borderTop: 1, borderColor: 'divider', gap: 2,
          }}
        >
          <Typography variant="body2" color="text.secondary">
            {someUnconfirmed
              ? t('du_bo_confirm_first', 'Open and confirm each consolidation before executing.')
              : allConfirmed
                ? t('du_bo_all_ready', 'All consolidations confirmed — ready to execute.')
                : ''}
          </Typography>
          <Button
            variant="contained"
            startIcon={<Play size={14} />}
            disabled={someUnconfirmed || totalApproved === 0}
            onClick={handleExecute}
            sx={{
              textTransform: 'none',
              backgroundColor: '#c9a96e',
              '&:hover': { backgroundColor: '#b8944f' },
              '&.Mui-disabled': { backgroundColor: '#e0e0e0' },
            }}
          >
            {t('du_bo_execute_approved', 'Execute approved')} ({totalApproved})
          </Button>
        </Box>
      </Paper>

      {/* The fullscreen preview viewer */}
      <BOConsolidationPreview />
    </Box>
  )
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function RowStatusIcon({ status }) {
  if (status === 'confirmed') return <CheckCircle2 size={16} color="#4caf50" />
  if (status === 'previewed') return <Eye size={16} color="#2196f3" />
  if (status === 'preview_failed') return <AlertTriangle size={16} color="#f44336" />
  return <Clock size={16} color="#9e9e9e" />
}

function RowStatusChip({ status, t }) {
  const config = {
    approved: { label: t('du_bo_status_approved', 'Awaiting preview'), color: 'default' },
    previewed: { label: t('du_bo_status_previewed', 'Preview ready'), color: 'info' },
    preview_failed: { label: t('du_bo_status_preview_failed', 'Preview failed'), color: 'error' },
    confirmed: { label: t('du_bo_status_confirmed', 'Confirmed'), color: 'success' },
  }[status] || { label: status, color: 'default' }

  return <Chip size="small" label={config.label} color={config.color} sx={{ height: 18, fontSize: '0.65rem' }} />
}
